"use client";

import { useNavigate } from "react-router-dom";
import {
  Building2,
  MoreVertical,
  Users,
  Target,
  MonitorPlay,
  ShieldPlus,
  UserMinus,
  Search,
  Info,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { useGetTenantsQuery } from "@/store/slices/apiSlice";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { expDarkIcon } from "@/lib/Images";
import CreateCampaignDialog from "@/components/dialogs/tenants/campaign/CreateCampaignDialog";
import { useState } from "react";
import { useDeactivateTenantMutation } from "@/store/slices/apiSlice";
import CircleLoader from "@/components/common/ActionLoader";
import { formatedStatus } from "@/lib/utils";

const TenantOrganisations = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const navigate = useNavigate();
  const { data, isLoading, isFetching } = useGetTenantsQuery(undefined, {
    refetchOnMountOrArgChange: true,
  });
  const tenants = data?.response?.data || [];
  // const filteredTenants = tenants.filter((tenant) =>
  //   tenant.orgName?.toLowerCase().includes(searchTerm.toLowerCase()),
  // );

  const filteredTenants = tenants
    .filter((tenant) =>
      tenant.orgName?.toLowerCase().includes(searchTerm.toLowerCase()),
    )
    .sort((a, b) => {
      const aInactive = a.status === "INACTIVE";
      const bInactive = b.status === "INACTIVE";

      if (aInactive && !bInactive) return 1;
      if (!aInactive && bInactive) return -1;

      return 0;
    });

  // const hasTenants = tenants.length > 0;
  const hasTenants = filteredTenants.length > 0;
  const handleDeactivateTenant = (tenant) => {
    console.log("Deactivate tenant:", tenant);
  };

  return (
    <div className="min-h-screen  px-8 py-4 text-neutral-100">
      {/* Header */}
      {/* <div className="mb-7 flex items-center gap-5"> */}
      <div className="mb-7 flex items-center justify-between gap-5">
        <h1 className="text-2xl font-bold tracking-tight">
          {/* Tenants Organisations */}
          Tenants
        </h1>
        <div className="relative w-full max-w-md">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-100/40" />

          <input
            type="text"
            placeholder="Search tenants..."
            className="h-10 w-full rounded-md border border-[#193D57] bg-brand-100 pl-10 pr-4 text-sm text-neutral-100 outline-none placeholder:text-neutral-100/40 focus:border-brand-15"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <Button
          type="button"
          onClick={() => navigate("/tenants/create-organisation")}
          className="h-8 rounded-md bg-gradient-to-r from-[#188CEB] to-[#3758E8] px-4 text-xs font-bold text-neutral-100 hover:opacity-90"
        >
          + Create New Tenant
        </Button>
      </div>

      {isLoading || isFetching ? (
        <div className="flex min-h-[520px] items-center justify-center">
          <CircleLoader loading={isLoading || isFetching} size={64} />
        </div>
      ) : !hasTenants ? (
        <EmptyTenantState />
      ) : (
        <div className="grid grid-cols-1 gap-8 xl:grid-cols-2">
          {/* {tenants.map((tenant) => ( */}
          {filteredTenants.map((tenant) => (
            <TenantCard
              key={tenant._id}
              tenant={tenant}
              navigate={navigate}
              onDeactivate={handleDeactivateTenant}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const EmptyTenantState = () => {
  return (
    <div className="flex min-h-[520px] items-center justify-center">
      <div className="text-center">
        <div className="mx-auto mb-8 flex h-[86px] w-[86px] items-center justify-center rounded-[24px] bg-gradient-to-br from-[#164669] to-[#33245F]">
          <Building2 className="h-12 w-12 text-brand-15" />
        </div>

        <h2 className="mb-4 text-2xl font-bold text-neutral-100/90">
          No organisations created yet
        </h2>

        <p className="text-sm font-medium text-neutral-100/90">
          Create an organisation to set up branding, campaigns, and experiences.
        </p>
      </div>
    </div>
  );
};

const TenantCard = ({ tenant, navigate, onDeactivate }) => {
  const [deactivateTenant] = useDeactivateTenantMutation();
  const [openCreateCampaign, setOpenCreateCampaign] = useState(false);
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [openActionsMenu, setOpenActionsMenu] = useState(false);

  const isTenantInactive = tenant.status === "INACTIVE";

  const getTenantDisplayStatus = (tenant) => {
    if (tenant.status === "INACTIVE") return "INACTIVE";

    // if (tenant.status === "ACTIVE" && tenant.activeStatus === "INACTIVE") {
    if (tenant.status === "ACTIVE" && Number(tenant?.setupStep) < 4) {
      return "SETUP_INCOMPLETE";
    }
    return "ACTIVE";
  };

  const tenantStatus = getTenantDisplayStatus(tenant);

  return (
    <div
      className={`rounded-lg bg-brand-100 px-6 py-6 shadow-lg transition-all ${
        tenant.highlighted
          ? "border border-brand-15 cursor-pointer"
          : "border border-[#193D57] cursor-pointer"
      } ${
        isTenantInactive
          ? "opacity-60 cursor-not-allowed"
          : ""
      }`}
    >
      {/* Top */}
      <div className="mb-5 flex items-start justify-between">
        <div className="flex items-center gap-3">
          <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-[#06324C] to-[#2F1852]">
            {tenant?.branding?.logoUrl ? (
              <img
                src={tenant?.branding?.logoUrl}
                alt={tenant.orgName}
                className="h-full w-full object-cover"
              />
            ) : (
              <Building2 className="h-8 w-8 text-brand-15" />
            )}
          </div>

          <h2 className="text-2xl font-bold">{tenant.orgName}</h2>
        </div>

        <DropdownMenu
          modal={false}
          open={isTenantInactive ? false : openActionsMenu}
          onOpenChange={(open) => {
            if (isTenantInactive) return;
            setOpenActionsMenu(open);
          }}
        >
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              disabled={isTenantInactive}
              aria-disabled={isTenantInactive}
              className={`flex h-10 w-10 items-center justify-center rounded-lg border border-brand-11 text-neutral-100 hover:bg-white/5 ${
                isTenantInactive
                  ? "cursor-not-allowed opacity-50 hover:bg-transparent"
                  : ""
              }`}
            >
              <MoreVertical className="h-5 w-5" />
            </button>
          </DropdownMenuTrigger>

          <DropdownMenuContent
            align="end"
            sideOffset={8}
            className="w-56 border border-brand-11 bg-brand-100 p-0 text-neutral-100"
          >
            <DropdownMenuItem
              className="flex cursor-pointer items-center gap-3 px-4 py-3 text-sm font-semibold text-neutral-100 focus:bg-[#121A45] focus:text-neutral-100"
              onClick={() =>
                navigate("/tenants/profile", {
                  state: {
                    tenantId: tenant._id,
                    onboardingConfiguration: tenant.onboardingConfiguration,
                  },
                })
              }
            >
              <Building2 className="h-5 w-5 text-neutral-100" />
              Go to tenant profile
            </DropdownMenuItem>

            <DropdownMenuItem
              className="flex cursor-pointer items-center gap-3 px-4 py-3 text-sm font-semibold text-neutral-100 focus:bg-[#121A45] focus:text-neutral-100"
              onClick={() => {
                setSelectedTenant(tenant);
                setOpenCreateCampaign(true);
              }}
            >
              <ShieldPlus className="h-5 w-5 text-neutral-100" />
              Add a new campaign
            </DropdownMenuItem>

            <DropdownMenuItem
              className="flex cursor-pointer items-center gap-3 px-4 py-3 text-sm font-semibold text-[#FF4D4D] focus:bg-[#2A1010] focus:text-[#FF4D4D]"
              onClick={async () => {
                try {
                  await deactivateTenant(tenant._id).unwrap();
                } catch (error) {
                  console.error(error);
                }
              }}
            >
              <UserMinus className="h-5 w-5 text-[#FF4D4D]" />
              Deactivate tenant
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Tags */}
      <div className="mb-5 flex items-center gap-3">
        {tenantStatus === "ACTIVE" ? (
          <span className="rounded-md bg-[#064D23] px-3 py-1 text-xs font-bold text-[#00E261]">
            ACTIVE
          </span>
        ) : tenantStatus === "SETUP_INCOMPLETE" ? (
          <span className="flex items-center gap-2 rounded-md border border-[#7A1E1E] bg-[#3A0D0D] px-3 py-1 text-xs font-bold text-[#FF5A5A]">
            <Info className="h-4 w-4" strokeWidth={3} />
            Tenant Setup Incomplete
          </span>
        ) : (
          <span className="rounded-md bg-[#FF4D4D33] px-3 py-1 text-xs font-bold text-[#FF4848]">
            INACTIVE
          </span>
        )}

        <span className="rounded-md border border-[#6D6500] bg-[#332F00] px-3 py-1 text-xs font-bold text-[#D4C300]">
          {tenant.industryType}
        </span>
      </div>

      <div className="border-t border-brand-18" />

      {/* Stats */}
      <div className="grid grid-cols-3 border-b border-brand-18 py-3">
        <TenantStat
          icon={<Users className="h-6 w-6 text-brand-15" />}
          value={`${tenant.noOfUsers} Users`}
        />

        <TenantStat
          icon={<Target className="h-6 w-6 text-brand-15" />}
          value={`${tenant.noOfCampaigns} Campaigns`}
          bordered
        />

        <TenantStat
          icon={<img src={expDarkIcon} className="h-6 w-6 text-brand-15" />}
          value={`${tenant.noOfExperiences} Experiences`}
        />
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between px-2 p-4 text-sm font-bold text-neutral-100/60">
        <span>
          Last active:{" "}
          {tenant?.lastActive
            ? new Date(tenant.lastActive).toLocaleDateString("en-US", {
                month: "short",
                day: "numeric",
                year: "numeric",
              })
            : "-"}
        </span>
        <span>
          Created{" "}
          {new Date(tenant.createdAt).toLocaleDateString("en-US", {
            month: "short",
            day: "numeric",
            year: "numeric",
          })}
        </span>
      </div>

      <CreateCampaignDialog
        open={openCreateCampaign}
        onClose={() => setOpenCreateCampaign(false)}
        tenantDetails={selectedTenant}
        onSuccess={(payload) => {}}
        onAddExperience={() => {
          setOpenAddExperience(true);
        }}
      />
    </div>
  );
};

const TenantStat = ({ icon, value, bordered }) => {
  return (
    <div
      className={`flex items-center justify-center gap-3 py-3 ${
        bordered ? "border-x border-brand-18" : ""
      }`}
    >
      {icon}
      <span className="text-sm font-bold text-neutral-100">{value}</span>
    </div>
  );
};

export default TenantOrganisations;
